/** Newsletter send time: store ISO timestamptz in DB; edit with datetime-local in UI. */

import { normalizeNewsletterStatus, newsletterStatusLabel } from './newsletterStatus';

const DEFAULT_TIME_LOCAL = '08:00';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toDatetimeLocalValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Value for `<input type="datetime-local" />` from DB (ISO or legacy YYYY-MM-DD).
 */
export function scheduledForToDatetimeLocal(val: string | null | undefined): string {
  if (!val?.trim()) return '';
  const trimmed = val.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T${DEFAULT_TIME_LOCAL}`;
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return '';
  return toDatetimeLocalValue(d);
}

/** Persist as ISO UTC string for `newsletters.scheduled_for` (timestamptz). */
export function datetimeLocalToScheduledFor(local: string): string | null {
  const t = local.trim();
  if (!t) return null;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function formatScheduledForDisplay(val: string | null | undefined): string {
  if (!val?.trim()) return 'Not scheduled';
  const d = new Date(val.trim());
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function isUpcomingScheduledFor(val: string | null | undefined): boolean {
  if (!val?.trim()) return false;
  const d = new Date(val.trim());
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() >= Date.now();
}

/** Human-readable relative time until (or since) a scheduled send instant. */
export function formatScheduledRelative(
  val: string | null | undefined,
  nowMs = Date.now(),
): string {
  if (!val?.trim()) return '';
  const d = new Date(val.trim());
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - nowMs;
  const future = diffMs >= 0;
  const absMs = Math.abs(diffMs);
  const minutes = Math.round(absMs / 60_000);
  if (minutes < 1) return future ? 'in less than a minute' : 'less than a minute ago';
  if (minutes < 60) {
    const label = `${minutes} minute${minutes === 1 ? '' : 's'}`;
    return future ? `in ${label}` : `${label} ago`;
  }
  const hours = Math.round(absMs / 3_600_000);
  if (hours < 48) {
    const label = `${hours} hour${hours === 1 ? '' : 's'}`;
    return future ? `in ${label}` : `${label} ago`;
  }
  const days = Math.round(absMs / 86_400_000);
  const label = `${days} day${days === 1 ? '' : 's'}`;
  return future ? `in ${label}` : `${label} ago`;
}

/**
 * Where a newsletter sits in the send pipeline. "Planned" means a send date
 * is saved but no scheduled_emails rows exist yet — nothing will go out.
 */
export type SendPipelinePhase =
  | 'no_date'
  | 'draft_planned'
  | 'draft_overdue'
  | 'planned'
  | 'overdue_planned'
  | 'queued'
  | 'on_hold'
  | 'sent';

export function getSendPipelinePhase(params: {
  status: string | null | undefined;
  scheduledFor: string | null | undefined;
  hasRecipientRows: boolean;
}): SendPipelinePhase {
  const status = normalizeNewsletterStatus(params.status);
  if (status === 'published') return 'sent';
  if (status === 'on_hold') return 'on_hold';
  if (status === 'ready_to_publish' || params.hasRecipientRows) return 'queued';
  if (!params.scheduledFor?.trim()) return 'no_date';
  const d = new Date(params.scheduledFor.trim());
  if (Number.isNaN(d.getTime())) return 'no_date';
  const overdue = d.getTime() < Date.now();
  if (status === 'draft') {
    return overdue ? 'draft_overdue' : 'draft_planned';
  }
  if (status === 'scheduled') {
    return overdue ? 'overdue_planned' : 'planned';
  }
  return overdue ? 'overdue_planned' : 'planned';
}

export type SendPipelineTone =
  | 'draft'
  | 'scheduled'
  | 'overdue-draft'
  | 'overdue-scheduled'
  | 'queued'
  | 'on-hold';

export interface SendPipelineSummary {
  headline: string;
  detail: string;
  autoQueueNote?: string;
  tone: SendPipelineTone;
}

/** Copy for the detail-page banner and header status line. */
export function getSendPipelineSummary(params: {
  phase: SendPipelinePhase;
  status: string;
  scheduledFor: string | null | undefined;
}): SendPipelineSummary | null {
  const { phase, status, scheduledFor } = params;
  const statusLabel = newsletterStatusLabel(status);

  if (phase === 'sent' || phase === 'no_date') return null;

  const absolute = formatScheduledForDisplay(scheduledFor);
  const relative = formatScheduledRelative(scheduledFor);
  const when =
    relative && absolute !== 'Not scheduled'
      ? `${relative} (${absolute})`
      : absolute !== 'Not scheduled'
        ? absolute
        : 'the planned time';

  if (phase === 'on_hold') {
    return {
      headline: 'On hold — send paused',
      detail:
        'Nothing queues or sends while On hold. Set status to Draft to keep editing, or Scheduled when you are ready to commit to the send date.',
      tone: 'on-hold',
    };
  }

  if (phase === 'queued') {
    return {
      headline: 'Ready to Publish · recipients queued',
      detail: `Send ${when}. Mail goes out on the next cron tick at or after that time. Review rows on the Recipients tab.`,
      tone: 'queued',
    };
  }

  const autoQueueNote = formatAutoMaterializeRunMessage(scheduledFor, { status: statusLabel });

  if (phase === 'draft_planned') {
    return {
      headline: `Draft · send date ${when}`,
      detail:
        'You have a send date, but status is still Draft — that date is calendar planning only. Nothing queues or sends until you set status to Scheduled in Settings (committed send) or use Queue recipients below.',
      tone: 'draft',
    };
  }

  if (phase === 'draft_overdue') {
    return {
      headline: `Draft · send date was ${when}`,
      detail:
        'The planned time has passed and status is still Draft, so no recipients were queued. Set status to Scheduled and queue recipients, or pick a new send date in Settings.',
      tone: 'overdue-draft',
    };
  }

  if (phase === 'planned') {
    return {
      headline: `Scheduled · send ${when}`,
      detail:
        'Status is Scheduled — you have committed to this send. Recipients are not queued yet; use Queue recipients below or wait for auto-queue about 24 hours before send.',
      tone: 'scheduled',
      autoQueueNote: autoQueueNote || undefined,
    };
  }

  if (phase === 'overdue_planned') {
    return {
      headline: `Scheduled · send date was ${when}`,
      detail:
        'You committed to send (Scheduled) but recipients are still not queued. Queue recipients now — auto-queue should have run in the last 24 hours if the cron is deployed.',
      tone: 'overdue-scheduled',
      autoQueueNote: autoQueueNote || undefined,
    };
  }

  return null;
}

/** One-line status under the header chips on the newsletter detail page. */
export function getSendPipelineHeaderLine(params: {
  phase: SendPipelinePhase;
  status: string;
  scheduledFor: string | null | undefined;
}): string | null {
  const summary = getSendPipelineSummary(params);
  if (!summary) return null;

  switch (summary.tone) {
    case 'draft':
      return 'Draft + send date — planning only; set status to Scheduled when you are committed to sending.';
    case 'scheduled':
      return 'Scheduled + send date — committed; auto-queue ~24h before send (every 30m) or Queue recipients in Actions.';
    case 'overdue-draft':
      return 'Draft + send date passed — still not committed; set Scheduled or queue recipients.';
    case 'overdue-scheduled':
      return 'Scheduled + send date passed — recipients not queued yet.';
    case 'queued':
      return 'Ready to Publish — recipients queued; mail goes out at the planned time.';
    case 'on-hold':
      return 'On hold — nothing queues or sends until you change status.';
    default:
      return null;
  }
}

/** Matches AUTO_MATERIALIZE_LEAD_HOURS default in the Inngest cron. */
export const DEFAULT_AUTO_MATERIALIZE_LEAD_HOURS = 24;

/** Matches auto-materialize Inngest cron (every 30 minutes). */
export const AUTO_MATERIALIZE_CRON_INTERVAL_MINUTES = 30;

/** Next auto-materialize cron tick (:00 and :30) strictly after `fromMs`. */
export function nextAutoMaterializeCronMs(fromMs = Date.now()): number {
  const d = new Date(fromMs);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  if (m < 30) {
    d.setMinutes(30, 0, 0);
  } else {
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
  }
  if (d.getTime() <= fromMs) {
    if (d.getMinutes() === 30) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0, 0, 0);
    } else {
      d.setMinutes(30, 0, 0);
    }
  }
  return d.getTime();
}

/** @deprecated Use nextAutoMaterializeCronMs — auto-queue is no longer hourly. */
export function nextHourlyCronMs(fromMs = Date.now()): number {
  return nextAutoMaterializeCronMs(fromMs);
}

/**
 * When the auto-materialize cron will queue recipients if they are still not
 * queued manually. Cron runs every 30 minutes; only applies when status is Scheduled.
 */
export function formatAutoMaterializeRunMessage(
  scheduledFor: string | null | undefined,
  options?: { leadHours?: number; nowMs?: number; status?: string | null },
): string {
  const status = (options?.status ?? '').trim();
  if (normalizeNewsletterStatus(status) !== 'scheduled') return '';
  const leadHours = options?.leadHours ?? DEFAULT_AUTO_MATERIALIZE_LEAD_HOURS;
  const nowMs = options?.nowMs ?? Date.now();
  if (!scheduledFor?.trim()) return '';

  const sendMs = new Date(scheduledFor.trim()).getTime();
  if (Number.isNaN(sendMs)) return '';

  const leadMs = leadHours * 3_600_000;
  const windowOpensMs = sendMs - leadMs;
  const nextCronLabel = formatScheduledForDisplay(new Date(nextAutoMaterializeCronMs(nowMs)).toISOString());

  if (nowMs >= windowOpensMs) {
    if (sendMs < nowMs) {
      return `Auto-queue runs every 30 minutes. Eligible now — next run ${nextCronLabel}.`;
    }
    return `Auto-queue runs every 30 minutes. Eligible within ${leadHours}h of send — next run ${nextCronLabel}.`;
  }

  const firstRunMs = nextAutoMaterializeCronMs(Math.max(nowMs, windowOpensMs - 1));
  const windowOpensLabel = formatScheduledForDisplay(new Date(windowOpensMs).toISOString());
  const firstRunLabel = formatScheduledForDisplay(new Date(firstRunMs).toISOString());
  const windowOpensRelative = formatScheduledRelative(new Date(windowOpensMs).toISOString(), nowMs);

  return `Auto-queue runs every 30 minutes. Recipients queue automatically starting ${windowOpensRelative} (${windowOpensLabel}, ${leadHours}h before send) — first cron tick about ${firstRunLabel}.`;
}
